import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import baseApi from 'shared/api/baseApi';
import { useAppDispatch, useAppSelector } from 'shared/hooks/redux';

import { useGetUserPermissionsQuery, useRefreshSessionMutation } from '../services/auth.api';
import { setPermissions, signOutUser, updateAccessToken } from '../store/user.slice';
import { checkIfTokenIsValid } from '../utils/authStorage';
import { hasAccess, RoutePermissionSet } from '../utils/permissions';
import { buildLoginRedirectPath } from '../utils/redirects';

interface AuthRouteProps {
  adminOnly?: boolean;
  permissions?: RoutePermissionSet;
}

const AuthRoute = ({ adminOnly = false, permissions }: AuthRouteProps) => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [refreshSession, { isLoading: isRefreshingSession }] = useRefreshSessionMutation();
  const recoveryAttemptedForToken = useRef<string | null>(null);
  const hasStoredSession = Boolean(user.accessToken);
  const userIsValid = checkIfTokenIsValid(user);
  const needsSessionRecovery = hasStoredSession && !userIsValid;
  const shouldFetchPermissions = hasStoredSession && userIsValid && !user.isAdmin && !!permissions;

  const {
    data: fetchedPermissions,
    isFetching: isFetchingPermissions,
    isLoading: isLoadingPermissions,
  } = useGetUserPermissionsQuery(user.id, { skip: !shouldFetchPermissions });

  useEffect(() => {
    if (!needsSessionRecovery || recoveryAttemptedForToken.current === user.accessToken) {
      return;
    }

    recoveryAttemptedForToken.current = user.accessToken;
    refreshSession()
      .unwrap()
      .then(({ accessToken }) => {
        dispatch(updateAccessToken(accessToken));
      })
      .catch(() => {
        dispatch(signOutUser());
        dispatch(baseApi.util.resetApiState());
      });
  }, [dispatch, needsSessionRecovery, refreshSession, user.accessToken]);

  useEffect(() => {
    if (shouldFetchPermissions && fetchedPermissions) {
      dispatch(setPermissions(fetchedPermissions));
    }
  }, [dispatch, fetchedPermissions, shouldFetchPermissions]);

  if (needsSessionRecovery || isRefreshingSession) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg-surface-alt text-sm font-medium text-text-muted">
        Restaurando sesión...
      </div>
    );
  }

  if (!hasStoredSession) {
    return <Navigate to={buildLoginRedirectPath(location.pathname, location.search)} replace />;
  }

  if (adminOnly && !user.isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (permissions && !user.isAdmin) {
    if (isLoadingPermissions || isFetchingPermissions) {
      return (
        <div className="min-h-screen grid place-items-center bg-bg-surface-alt text-sm font-medium text-text-muted">
          Cargando permisos...
        </div>
      );
    }

    const currentPermissions = fetchedPermissions?.permissions ?? user.permissions;
    if (!hasAccess(currentPermissions, permissions)) {
      if (permissions.redirectOnDeny) {
        return <Navigate to={permissions.redirectOnDeny} replace />;
      }

      return (
        <div className="min-h-screen grid place-items-center bg-bg-surface-alt text-center">
          <section className="max-w-sm px-6">
            <p className="text-xs font-bold uppercase tracking-widest text-text-subtle mb-2 font-body">
              Acceso restringido
            </p>
            <h1 className="text-2xl font-bold text-text-default m-0 font-display">
              No tienes permisos para Ops Center
            </h1>
          </section>
        </div>
      );
    }
  }

  return <Outlet />;
};

export default AuthRoute;
